import { useCallback, useEffect, useRef, useState } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import type {
  IAgoraRTCClient,
  ILocalAudioTrack,
  ILocalVideoTrack,
  UID,
} from 'agora-rtc-sdk-ng';
import type { JoinTokenResponse } from '@edytech/shared/types';
import { TOKEN_REFRESH_BEFORE_MS } from '@edytech/shared/constants';

/**
 * Phase 2 — Agora RTC room lifecycle (TRD §6.3/§6.6).
 * - host publishes; audience subscribes (broadcast mode) or also publishes (interactive)
 * - token re-fetch before `expiresAt - 10m` while the session is ONGOING
 * - Leave unpublishes, leaves the channel and destroys the client
 * - `stub` (dev placeholder keys) simulates the room without a real Agora project
 *
 * The client only ever holds the signed `rtcToken`; the Agora app id is
 * derived from the token at runtime (Agora "006" format embeds it after the
 * 3-char version), so app ids/certificates never live in client bundles.
 */

export type RoomPhase =
  | 'loading'
  | 'lobby'
  | 'connecting'
  | 'inRoom'
  | 'left'
  | 'ended'
  | 'blocked';

export interface RoomError {
  kind: 'entitlement' | 'window' | 'ended' | 'device' | 'unknown';
  message: string;
}

export interface RemoteUser {
  uid: number;
  hasVideo: boolean;
  hasAudio: boolean;
  displayName: string;
}

export function toNumericUid(uid: UID): number {
  const n = Number(uid);
  return Number.isFinite(n) ? n : -1;
}

export interface JoinRoomOptions {
  joinMuted: boolean;
}

export interface UseLiveClassRoomArgs {
  sessionId?: number;
  getJoinToken: () => Promise<JoinTokenResponse>;
  /** Called when the server reports the session ended (410 on refresh). */
  onSessionEnded?: () => void;
}

/** Agora "006" tokens embed the 32-char app id right after the version prefix. */
export function appIdFromToken(token: string): string {
  return token.slice(3, 35);
}

/** Graceful block for Safari < 14.1 (TRD §6.6 browser support). */
export function isSafariTooOld(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const m = ua.match(/Version\/(\d+\.\d+).*Safari/);
  if (!m || /Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
  const version = Number.parseFloat(m[1]);
  return Number.isFinite(version) && version < 14.1;
}

/** Normalize axios/fetch errors to an HTTP status when available. */
export function getErrorStatus(e: unknown): number | null {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { status?: number } }).response;
    if (resp?.status) return resp.status;
  }
  if (e && typeof e === 'object' && 'status' in e) {
    const s = (e as { status?: number }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}

function isDevicePermissionError(e: unknown): boolean {
  if (e && typeof e === 'object' && 'name' in e) {
    const name = String((e as { name?: unknown }).name ?? '');
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'NotFoundError') {
      return true;
    }
  }
  const msg = e instanceof Error ? e.message : String(e);
  return /permission|NotAllowedError|device/i.test(msg);
}


export function useLiveClassRoom({ getJoinToken, onSessionEnded }: UseLiveClassRoomArgs) {
  const [phase, setPhase] = useState<RoomPhase>('lobby');
  const [token, setToken] = useState<JoinTokenResponse | null>(null);
  const [remoteUsers, setRemoteUsers] = useState<RemoteUser[]>([]);
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [error, setError] = useState<RoomError | null>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<ILocalVideoTrack | null>(null);

  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localAudioRef = useRef<ILocalAudioTrack | null>(null);
  const localVideoRef = useRef<ILocalVideoTrack | null>(null);
  const tokenRef = useRef<JoinTokenResponse | null>(null);
  const sessionEndedRef = useRef(false);
  const joinedRef = useRef(false);
  // Drives the "refresh before expiresAt - 10m" timer (TRD §6.6) via an effect.
  const [refreshAt, setRefreshAt] = useState<number | null>(null);

  const stub = !!token?.stub;
  const isHost = token?.role === 'host';
  // In interactive mode everyone may publish; in broadcast mode only the host does.
  const canPublish = !!token && (token.role === 'host' || token.mode === 'interactive');

  /** Tear down the RTC client (used by leave + token-refresh ended handling). */
  const leaveClient = useCallback(async () => {
    setRefreshAt(null);
    joinedRef.current = false;
    const client = clientRef.current;
    clientRef.current = null;

    try {
      await localAudioRef.current?.close();
      await localVideoRef.current?.close();
    } catch {
      /* ignore */
    }
    localAudioRef.current = null;
    localVideoRef.current = null;
    setLocalVideoTrack(null);
    setMicMuted(false);
    setCameraOff(false);

    if (client) {
      try {
        client.removeAllListeners();
        await client.leave();
      } catch {
        /* already left */
      }
    }
  }, []);

  // Token refresh loop: fires at expiresAt - TOKEN_REFRESH_BEFORE_MS while the
  // session is ONGOING; the server renews only while ONGOING, so a 410 means
  // the session ended and we drop out gracefully.
  useEffect(() => {
    if (refreshAt === null) return;
    const delay = Math.max(0, refreshAt - Date.now());
    const id = setTimeout(() => {
      void (async () => {
        try {
          const fresh = await getJoinToken();
          if (fresh.rtcToken !== tokenRef.current?.rtcToken) {
            // Renew the running client without re-joining.
            await clientRef.current?.renewToken(fresh.rtcToken);
          }
          tokenRef.current = fresh;
          setToken(fresh);
          setRefreshAt(new Date(fresh.expiresAt).getTime() - TOKEN_REFRESH_BEFORE_MS);
        } catch (e) {
          const status = getErrorStatus(e);
          if (status === 410) {
            sessionEndedRef.current = true;
            await leaveClient();
            setPhase('ended');
            onSessionEnded?.();
          } else {
            // Transient failure — retry shortly instead of dropping the class.
            setRefreshAt(Date.now() + 60_000);
          }
        }
      })();
    }, delay);
    return () => clearTimeout(id);
  }, [refreshAt, getJoinToken, onSessionEnded, leaveClient]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      void leaveClient();
    };
  }, [leaveClient]);

  // Fetch the join-token once on mount — the server is authoritative for
  // entitlement + join window; 410 -> ended, 403 -> out-of-window/blocked.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setPhase('loading');
      try {
        const tk = await getJoinToken();
        if (cancelled) return;
        tokenRef.current = tk;
        setToken(tk);
        setPhase('lobby');
      } catch (e) {
        if (cancelled) return;
        const status = getErrorStatus(e);
        if (status === 410) {
          sessionEndedRef.current = true;
          setPhase('ended');
          onSessionEnded?.();
        } else if (status === 403) {
          setError({
            kind: 'window',
            message: e instanceof Error ? e.message : 'This session is not accepting joins right now',
          });
          setPhase('lobby');
        } else if (status === 401) {
          setError({ kind: 'entitlement', message: 'Please sign in to join this session' });
          setPhase('lobby');
        } else {
          setError({
            kind: 'unknown',
            message: e instanceof Error ? e.message : 'Failed to load the session',
          });
          setPhase('lobby');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getJoinToken, onSessionEnded]);

  const joinRoom = useCallback(
    async ({ joinMuted }: JoinRoomOptions) => {
      if (!token || joinedRef.current) return;

      if (stub) {
        // Dev stub — simulate the room (no real Agora project needed).
        setMicMuted(joinMuted);
        setCameraOff(false);
        setPhase('inRoom');
        setRefreshAt(new Date(token.expiresAt).getTime() - TOKEN_REFRESH_BEFORE_MS);
        return;
      }

      setPhase('connecting');
      setDeviceError(null);
      setError(null);

      try {
        const client = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
        clientRef.current = client;
        await client.setClientRole(canPublish ? 'host' : 'audience');

        client.on('user-published', async (user, mediaType) => {
          await client.subscribe(user, mediaType);
          if (mediaType === 'video' && user.videoTrack) {
            user.videoTrack.play(`remote-video-${toNumericUid(user.uid)}`);
          }
          if (mediaType === 'audio' && user.audioTrack) {
            user.audioTrack.play();
          }
          const uid = toNumericUid(user.uid);
          setRemoteUsers((prev) => {
            const existing = prev.find((u) => u.uid === uid);
            if (existing) {
              return prev.map((u) =>
                u.uid === uid
                  ? {
                      ...u,
                      hasVideo: mediaType === 'video' ? true : u.hasVideo,
                      hasAudio: mediaType === 'audio' ? true : u.hasAudio,
                    }
                  : u
              );
            }
            return [
              ...prev,
              {
                uid,
                hasVideo: mediaType === 'video',
                hasAudio: mediaType === 'audio',
                displayName: `User ${uid}`,
              },
            ];
          });
        });

        client.on('user-unpublished', (user, mediaType) => {
          const uid = toNumericUid(user.uid);
          setRemoteUsers((prev) =>
            prev.map((u) =>
              u.uid === uid
                ? {
                    ...u,
                    hasVideo: mediaType === 'video' ? false : u.hasVideo,
                    hasAudio: mediaType === 'audio' ? false : u.hasAudio,
                  }
                : u
            )
          );
        });

        client.on('user-left', (user) => {
          const uid = toNumericUid(user.uid);
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== uid));
        });

        await client.join(appIdFromToken(token.rtcToken), token.channel, token.rtcToken, token.uid);
        joinedRef.current = true;

        // Publish local tracks (hosts always; audience only in interactive mode).
        if (canPublish) {
          const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
          localAudioRef.current = audioTrack;
          localVideoRef.current = videoTrack;
          setLocalVideoTrack(videoTrack);
          await client.publish([audioTrack, videoTrack]);
          // Join-muted: keep video but mute audio immediately.
          if (joinMuted) {
            await audioTrack.setMuted(true);
          }
        }
        setMicMuted(joinMuted);

        tokenRef.current = token;
        setPhase('inRoom');
        setRefreshAt(new Date(token.expiresAt).getTime() - TOKEN_REFRESH_BEFORE_MS);
      } catch (e) {
        console.error('join failed', e);
        await leaveClient();
        if (isDevicePermissionError(e)) {
          setDeviceError('Microphone/camera access was denied. Enable devices and try again.');
          setPhase('lobby');
        } else {
          setError({
            kind: 'unknown',
            message: e instanceof Error ? e.message : 'Failed to join the room',
          });
          setPhase('lobby');
        }
      }
    },
    [token, stub, canPublish, leaveClient]
  );

  const leaveRoom = useCallback(async () => {
    await leaveClient();
    setPhase('left');
  }, [leaveClient]);

  const toggleMic = useCallback(async () => {
    const track = localAudioRef.current;
    if (!track) return;
    const muted = !micMuted;
    await track.setMuted(muted);
    setMicMuted(muted);
  }, [micMuted]);

  const toggleCamera = useCallback(async () => {
    const track = localVideoRef.current;
    if (!track) return;
    const off = !cameraOff;
    await track.setEnabled(!off);
    setCameraOff(off);
  }, [cameraOff]);

  return {
    phase,
    setPhase,
    token,
    remoteUsers,
    micMuted,
    cameraOff,
    deviceError,
    error,
    setError,
    stub,
    isHost,
    canPublish,
    localVideoTrack,
    joinRoom,
    leaveRoom,
    leaveClient,
    toggleMic,
    toggleCamera,
  };
}

